import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { X, Building2, Save, Pencil } from "lucide-react";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useTenantDetail,
  useCurrencies,
  useDateFormats,
  useTimezones,
  useUpdateTenantDetail,
} from "../hooks";
import { UpdateTenantRequest } from "../types";
import { queryClient } from "@shared/api/queries/queryClient";
import {
  locationJsonService,
  LocationOption,
} from "@shared/services/locationService";
import { PhoneInputComponent } from "@shared/components/PhoneInputComponent";
import { authApi } from "@features/auth/api";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { globalQueryClient } from "@shared/api/queries/queryClients";
interface OrganizationDetailsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onEditClick?: () => void;
}
const OrganizationDetailsModal: React.FC<OrganizationDetailsModalProps> = ({
  isOpen,
  onOpenChange,
  onEditClick,
}) => {
  const { data: tenantData, isLoading: tenantLoading } = useTenantDetail();
  const { data: currencies, isLoading: currenciesLoading } = useCurrencies();
  const { data: dateFormats, isLoading: dateFormatsLoading } = useDateFormats();
  const { data: timezones, isLoading: timezonesLoading } = useTimezones();
  const updateTenantMutation = useUpdateTenantDetail();
  // Location state
  const [countries] = useState<LocationOption[]>(() =>
    locationJsonService.getCountries(),
  );
  const [states, setStates] = useState<LocationOption[]>([]);
  const [cities, setCities] = useState<LocationOption[]>([]);
  const [isFormReady, setIsFormReady] = useState(false);
  // Create form fields function
  const getOrganizationFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Organization Name",
      required: true,
      placeholder: "Acme Corporation",
      defaultValue: "",
      helperText: "Pre-filled from your registration",
      readonly: true,
      fullWidth: true,
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-12 px-4 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "adminEmail",
      type: "email",
      label: "Admin Email",
      required: true,
      placeholder: "admin@acmecorp.com",
      defaultValue: "",
      helperText: "Pre-filled from your registration",
      fullWidth: true,
      readonly: true,
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-12 px-4 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },

    {
      name: "contact_number",
      type: "phone" as const, // Changed from "phone" to "custom"
      label: "Contact Number",
      required: true,
      placeholder: "Enter phone number",
      defaultValue: "",
      fullWidth: true,
      helperText: "Enter a valid mobile or business contact number.",
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },

      validation: {
        pattern: {
          value: /^[0-9]+$/,
        },
        maxLength: {
          value: 15,
        },
      },
      customRender: (field, form, error) => (
        <div className="w-full">
          <PhoneInputComponent
            value={form.getValues(field.name)}
            onChange={(val: any) => {
              form.setValue(field.name, val);
              // Clear any existing errors when user types
              if (error) {
                form.clearErrors(field.name);
              }
            }}
            placeholder={field.placeholder}
            className={`w-full h-12 border rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 text-gray-900 ${
              error ? "border-red-500" : "border-gray-300"
            }`}
          />
        </div>
      ),
    },
    {
      name: "country",
      type: "select",
      label: "Country",
      required: true,
      placeholder: "Select Country",
      defaultValue: "",
      fullWidth: true,
      options: countries,
      helperText: "Select the country where your organization is registered.",
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "currency",
      type: "select",
      label: "Currency",
      required: true,
      placeholder: "Select Currency",
      defaultValue: "",
      fullWidth: true,
      helperText:
        "This currency will be applied across contracts, billing workflows, and financial statements.",
      options:
        currencies?.result?.map((currency) => ({
          value: currency.key,
          label: currency.value,
        })) || [],
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "date_format",
      type: "select",
      label: "Date Format",
      required: true,
      placeholder: "MM/DD/YYYY",
      defaultValue: "",
      fullWidth: true,
      helperText: "Choose how dates should appear across the platform.",
      options:
        dateFormats?.result?.map((format) => ({
          value: format.key,
          label: format.value,
        })) || [],
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "timezone",
      type: "select",
      label: "Time Zone",
      required: true,
      placeholder: "Eastern Time (UTC-5)",
      defaultValue: "",
      fullWidth: true,
      helperText: "Used for scheduling, workflows, and system timestamps.",
      options:
        timezones?.result?.map((timezone) => ({
          value: timezone.key,
          label: timezone.value,
        })) || [],
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
  ];
  const form = FormService.createForm({
    fields: getOrganizationFields,
    mode: "onChange",
    validateOnMount: false,
  });
  // Helper function to find value by label (for backward compatibility)
  const findValueByLabel = (
    options: LocationOption[],
    label: string,
  ): string => {
    const option = options.find(
      (opt) => opt.label === label || opt.value === label,
    );
    return option?.value || label || "";
  };
  // Main useEffect for handling modal open and data population
  useEffect(() => {
    if (!isOpen) {
      // Reset everything when modal closes
      setStates([]);
      setCities([]);
      setIsFormReady(false);
      return;
    }
    if (!tenantData?.result) return;
    const loadLocationDataAndForm = async () => {
      setIsFormReady(false);

      const tenant = tenantData.result;
      const countryValue = tenant.extraDataMap?.country ?? "";
      const stateValue = tenant.extraDataMap?.state ?? "";
      const cityValue = tenant.extraDataMap?.city ?? "";
      let newStates: LocationOption[] = [];
      let newCities: LocationOption[] = [];
      let finalStateValue = "";
      let finalCityValue = "";
      // Load states if country exists
      if (countryValue) {
        newStates = locationJsonService.getStatesByCountry(countryValue);
        setStates(newStates);

        // Find the correct state value (handle both label and value cases)
        finalStateValue = findValueByLabel(newStates, stateValue);
        // Load cities if state exists
        if (stateValue && finalStateValue) {
          newCities = locationJsonService.getCitiesByState(
            countryValue,
            finalStateValue,
          );
          setCities(newCities);

          // Find the correct city value (handle both label and value cases)
          finalCityValue = findValueByLabel(newCities, cityValue);
        }
      }
      // Wait for state updates to complete
      await new Promise((resolve) => setTimeout(resolve, 100));
      // Reset form with all values
      form.reset({
        name: tenant.name || "",
        adminEmail: tenant.userSpoc?.email || "",
        contact_number: tenant.contactNumber || "",
        country: countryValue,
        state: finalStateValue,
        city: finalCityValue,
        currency: tenant.currency || "",
        date_format: tenant.dateFormat || "",
        timezone: tenant.extraDataMap?.timezone || "",
      });
      console.log("Form reset with values:", {
        country: countryValue,
        state: finalStateValue,
        city: finalCityValue,
        statesAvailable: newStates.length,
        citiesAvailable: newCities.length,
      });
      setIsFormReady(true);
    };
    loadLocationDataAndForm();
  }, [isOpen, tenantData, form]);
  const handleSubmit = async (data: any) => {
    console.log("Submitting data:", data);

    const updateData: UpdateTenantRequest = {
      name: data.name,
      contact_number: data.contact_number, // Fixed: was contactNumber
      currency: data.currency,
      date_format: data.date_format,
      address_map: {
        country: data.country,
        state: data.state,
        city: data.city,
      },
      extra_data_map: {
        country: data.country,
        state: data.state,
        city: data.city,
        timezone: data.timezone,
      },
    };
    updateTenantMutation.mutate(updateData, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ["onboarding", "tenant-detail"],
        });
        onOpenChange(false);

        // 2. Fetch updated profile to capture new tenant settings
        try {
          const profile = await authApi.getUserProfile();
          if (profile) {
            // Update React Query local cache
            queryClient.setQueryData(["userProfile"], profile);
            
            // Update global cache
            const profileKey = QueryKeyFactory.global.user.profile();
            globalQueryClient.setQueryData(profileKey, profile);
            
            // Update localStorage
            localStorage.setItem("cached_user_profile", JSON.stringify(profile));
          }
        } catch (err) {
          console.error("Failed to refresh user profile after tenant update:", err);
        }
      },
    });
  };
  const handleFieldChange = (fieldName: string, value: any) => {
    // Clear field errors
    if (FormService.getFieldError(form, fieldName)) {
      form.clearErrors(fieldName);
    }
    if (fieldName === "country") {
      if (value) {
        const newStates = locationJsonService.getStatesByCountry(value);
        setStates(newStates);

        // Only clear city if we're changing to a different country
        const currentCountry = form.getValues().country;
        if (currentCountry !== value) {
          setCities([]);
          form.setValue("state", "");
          form.setValue("city", "");
        }
      } else {
        setStates([]);
        setCities([]);
        form.setValue("state", "");
        form.setValue("city", "");
      }
    }
    if (fieldName === "state") {
      const countryValue = form.getValues().country;
      if (value && countryValue) {
        const newCities = locationJsonService.getCitiesByState(
          countryValue,
          value,
        );
        setCities(newCities);

        // Only clear city if we're changing to a different state
        const currentState = form.getValues().state;
        if (currentState !== value) {
          form.setValue("city", "");
        }
      } else {
        setCities([]);
        form.setValue("city", "");
      }
    }
    // For city field, no special handling needed - just let it update normally
    if (fieldName === "city") {
      console.log("City selected:", value);
    }
  };
  const handleReset = () => {
    form.reset();
    setStates([]);
    setCities([]);
    setIsFormReady(false);
  };
  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 pt-6 mt-8 border-t border-gray-200">
      <Button
        type="button"
        variant="outline"
        onClick={() => onOpenChange(false)}
        disabled={updateTenantMutation.isPending}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        disabled={updateTenantMutation.isPending}
        className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
      >
        {updateTenantMutation.isPending ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            Saving...
          </>
        ) : (
          <>
            <Save className="h-4 w-4" />
            Save Settings
          </>
        )}
      </Button>
    </div>
  );
  const isLoading =
    tenantLoading ||
    currenciesLoading ||
    dateFormatsLoading ||
    timezonesLoading;
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-4 border-b shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center bg-purple-100 text-primary rounded-full p-2 mr-1">
                <Building2 className="h-5 w-5" />
              </span>
              <h2 className="text-lg font-semibold">Organisation Details</h2>
            </div>
            {onEditClick && (
              <button
                onClick={() => onOpenChange(false)}
                disabled={updateTenantMutation.isPending}
                className="h-8 w-8 rounded-full hover:bg-gray-100 flex items-center justify-center"
              >
                ✕
              </button>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Configure your organization settings and preferences.
          </p>
        </div>

        {/* Scrollable Form Body */}
        <div className="overflow-y-auto p-6 flex-1">
          {isLoading ? (
            <div className="space-y-6">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
                  <div className="h-12 bg-gray-200 rounded-lg" />
                </div>
              ))}
            </div>
          ) : isFormReady ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DynamicForm
                fields={getOrganizationFields}
                form={form}
                onSubmit={handleSubmit}
                onReset={handleReset}
                onFieldChange={handleFieldChange}
                loading={updateTenantMutation.isPending}
                gridColumns={2}
                responsiveColumns={{ sm: 1, md: 2 }}
                config={{
                  showProgress: false,
                  layout: "vertical",
                  spacing: "none",
                  submitButtonText: "Save Settings",
                  showResetButton: false,
                  resetOnSubmit: false,
                  classes: {
                    form: "space-y-0",
                    fieldContainer: "",
                    buttonContainer: "hidden", // we'll use custom footer
                    fieldGroup: "",
                  },
                }}
                renderCustomFooter={null}
                className="col-span-2"
                style={{
                  background: "transparent",
                  border: "none",
                  boxShadow: "none",
                }}
              />
            </div>
          ) : (
            <div className="space-y-6 text-center text-gray-500">
              Loading form...
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="border-t bg-white p-4 flex justify-end gap-4 shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateTenantMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={form.handleSubmit(handleSubmit)}
            disabled={updateTenantMutation.isPending}
          >
            Save Settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default OrganizationDetailsModal;
